import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  ArrowLeft,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Shield,
  Key,
  Sparkles,
  Lock,
} from "lucide-react";
import { userService } from "../../api/services/userService";
import { useToast } from "../../components/common/ThemeToaster";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const {
    success: toastSuccess,
    error: toastError,
    info: toastInfo,
  } = useToast();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [touched, setTouched] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getEmailError = () => {
    if (!touched) return null;
    if (!email) return "Email is required";
    if (!validateEmail(email)) return "Please enter a valid email address";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);

    if (!email || !validateEmail(email)) {
      toastError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      // Call your password reset API
      await userService.forgotPassword({ email });

      setIsSubmitted(true);
      toastSuccess("Reset link sent! Check your email inbox.");
      toastInfo("The link will expire in 1 hour for security.");
    } catch (err) {
      console.error("Forgot password error:", err);

      let errorMessage = "Failed to send reset link. Please try again.";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      toastError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full relative z-10"
      >
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => navigate("/user/login")}
          className="group flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary-600 transition-colors mb-6"
        >
          <div className="p-1.5 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
            <ArrowLeft
              size={16}
              className="group-hover:text-primary-600 transition-colors"
            />
          </div>
          <span>Back to Login</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden"
        >
          {/* Header */}
          <div className="relative p-6 sm:p-8 bg-gradient-to-r from-primary-600/5 to-secondary-600/5 border-b border-gray-100 dark:border-gray-800">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-full mb-4">
                <Key size={14} className="text-primary-600" />
                <span className="text-xs font-medium text-primary-600">
                  Reset Password
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Forgot Password?
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                No worries! Enter your email and we'll send you a reset link.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {!isSubmitted ? (
              <form onSubmit={handleSubmit}>
                {/* Email Field */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <Mail size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setTouched(true);
                      }}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full px-4 py-3.5 pl-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border rounded-xl transition-all duration-200
          ${
            getEmailError()
              ? "border-red-500 focus:ring-red-500"
              : focusedField === "email"
                ? "border-primary-500 ring-2 ring-primary-500/20"
                : "border-gray-200/50 dark:border-gray-700/50 focus:border-primary-500"
          } focus:outline-none focus:ring-2 transition-all text-gray-900 dark:text-white placeholder-gray-400`}
                      placeholder="john@company.com"
                    />
                  </div>
                  {getEmailError() && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {getEmailError()}
                    </p>
                  )}
                </div>

                {/* Info Note */}
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Shield size={18} className="text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">
                        Security Note
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-300">
                        We'll send a password reset link to your email. The link
                        will expire in 1 hour for security reasons.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-primary-500/20"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span>Sending reset link...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Send Reset Link</span>
                    </>
                  )}
                </button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-4"
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle size={40} className="text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Check Your Email
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We've sent a password reset link to:
                </p>
                <p className="font-medium text-primary-600 dark:text-primary-400 mb-6">
                  {email}
                </p>
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 mb-6">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Didn't receive the email? Check your spam folder or{" "}
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="font-medium underline hover:no-underline"
                    >
                      try again
                    </button>
                  </p>
                </div>
                <Link
                  to="/user/login"
                  className="inline-flex items-center gap-2 text-primary-600 font-medium hover:gap-3 transition-all"
                >
                  Return to Sign In
                  <ArrowLeft size={16} className="rotate-180" />
                </Link>
              </motion.div>
            )}

            {/* Footer Note */}
            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <Lock size={12} />
                <span>Secure password recovery by TradeBridge</span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
